import React, { useCallback, useRef, useState } from 'react';
import { useTriggerOnClickOutsideElement } from '@lmt/unicorn-shared/app/helpers/useTriggerOnClickOutsideElement';
import { DefaultProps, MandatoryProps } from './types';
import { InputTemplateOptionList } from '../../../input-templates/list/InputTemplateOptionList';
import { Popper } from '@mui/material';
import { Input } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/Input';
import { InputTemplateBadge } from '../../../input-templates/badge/InputTemplateBadge';

type NumberInputProps = MandatoryProps &
  Partial<DefaultProps> & {
    text: number;
    setText: (value: string) => void;
    setDataBlockFieldValue?: (key: string, value: number | undefined) => void;
    error?: string;
    popperClassName?: string;
  };

export const EditableNumberInput: React.FC<NumberInputProps> = React.memo(
  function EditableNumberInput(props: NumberInputProps) {
    const { text, setText, setDataBlockFieldValue, error } = props;
    const [expanded, setExpanded] = useState<boolean>(false);
    const optionListRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const hide = useCallback(() => setExpanded(false), [setExpanded]);
    useTriggerOnClickOutsideElement(optionListRef, hide, expanded);
    const onTemplateClickHandler = useCallback(() => {
      setExpanded((oldExpandedValue) => {
        return !oldExpandedValue;
      });
    }, []);

    const templateBadge = props.dataBlockFieldId && (
      <InputTemplateBadge
        onClick={onTemplateClickHandler}
        isSelected={expanded}
      />
    );

    return (
      <div>
        <Input
          value={text ?? null}
          errorText={error}
          onChange={(e) => {
            const value = Number.parseFloat(e.target?.value);
            setText(e.target?.value);
            setDataBlockFieldValue?.(props.dataKey, value);
          }}
          ref={inputRef}
          endAdornment={props.dataBlockFieldId ? templateBadge : undefined}
          endAdornmentVisibleOnHover={!expanded}
          type="number"
        />
        <Popper
          className={props.popperClassName}
          open={expanded}
          placement="bottom-start"
          anchorEl={inputRef.current}
          sx={{ width: inputRef.current?.offsetWidth }}
        >
          <InputTemplateOptionList
            fieldId={props.dataBlockFieldId}
            fieldName={props.dataKey}
            isNumber={true}
            onOptionClick={(value: string) => {
              const numberValue = Number.parseFloat(value);
              setExpanded(false);
              setText(value);
              setDataBlockFieldValue?.(props.dataKey, numberValue);
            }}
            elementRef={optionListRef}
            onClose={() => setExpanded(false)}
            isMultiline={false}
          />
        </Popper>
      </div>
    );
  },
);
